import { c as _c } from "react/compiler-runtime";
import { createLogger } from '@svenskaspelab/nova-core/log';
import { environmentManager, useQueryClient, useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
const logger = createLogger('tanstack-suspense-query');
/*

export type PublicSuspenseQueryOptions<TQueryFnData = unknown, TData = TQueryFnData> = {
  select?: (data: TQueryFnData) => TData;
};
*/
/**
 * A wrapper around tanstack query useSuspenseQuery to avoid leaking framework specific code.
 * Should always be used from every publicly exposed query.
 * useSuspenseQuery is useful for non critical data where data can be background fetched.
 * Loading states is rendered with &lt;Suspense> boundary and errors is caught with ErrorBoundary.
 *
 * We favor useSuspenseQuery over useQuery in most cases. However, for non live data Nextjs server components should be used in first hand.
 * At the time of development, there are two ways to utilize useSuspenseQuery in a Next.js application with server components, we have
 * chosen to go with the first approach since it's the most mature way.
 *
 * 1. Prefetch data (not awaited) in a server component, then useSuspenseQuery in a client component.
 *    This approach is a bit verbose and boilerplate but is more performant than the second approach.
 *    [Suspense guide](https://tanstack.com/query/v5/docs/framework/react/guides/suspense).
 * 2. Use an experimental ReactQueryStreamedHydration that is mounted near the root of the application.
 *    This approach provides great DX and is easier to understand, but since it is experimental we try to avoid it.
 *    Prefetching is not necessary. This approach has an impact on performance (if not using suspense) but provides better DX.
 *    [Experimental streaming without prefetching in Next.js](https://tanstack.com/query/v5/docs/framework/react/guides/advanced-ssr#experimental-streaming-without-prefetching-in-nextjs).
 * 3. [Experimental useQuery promise](https://tanstack.com/query/v5/docs/framework/react/guides/suspense#using-usequerypromise-and-reactuse-experimental).
 *
 * @param options tanstack query options
 * @param options.requiresServerPrefetch Set to false when the queryFn resolves locally, to opt out of the missing prefetch diagnostic
 * @param queryClient tanstack query client
 * @returns query result api
 */
export function useSuspenseQuery(options, queryClient) {
  const $ = _c(7);
  const client = useQueryClient(queryClient);
  let queryOptions;
  if ($[0] !== client || $[1] !== options) {
    const {
      requiresServerPrefetch: t0,
      ...t1
    } = options;
    queryOptions = t1;
    const requiresServerPrefetch = t0 === undefined ? true : t0;
    if (requiresServerPrefetch && environmentManager.isServer()) {
      const cached = client.getQueryCache().get(client.defaultQueryOptions(queryOptions).queryHash);
      if (cached === undefined || cached.state.status === "pending" && cached.state.fetchStatus === "idle") {
        logger.warn("Suspense query is not seeded on the server render", {
          queryKey: queryOptions.queryKey,
          cacheState: cached === undefined ? "missing" : "pending-idle"
        });
      }
    }
    $[0] = client;
    $[1] = options;
    $[2] = queryOptions;
  } else {
    queryOptions = $[2];
  }
  const query = useTanstackSuspenseQuery(queryOptions, queryClient);
  let t0;
  if ($[3] !== query.data || $[4] !== query.isError || $[5] !== query.isLoading) {
    t0 = {
      data: query.data,
      isLoading: query.isLoading,
      isError: query.isError
    };
    $[3] = query.data;
    $[4] = query.isError;
    $[5] = query.isLoading;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  return t0;
}